import { useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, Loader2 } from "lucide-react";

import { WhatsappIcon, WHATSAPP_BTN } from "@/components/common/whatsapp-brand";
import { capture } from "@/lib/posthog";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// El `optinUrl` ya trae el número de Licitigo y el mensaje; se leen de ahí para
// mostrarlos como respaldo manual en vez de pedirle otro campo al backend.
function parseOptinUrl(
  url: string,
): { number: string; message: string } | null {
  try {
    const parsed = new URL(url);
    const message = parsed.searchParams.get("text");
    const number = parsed.pathname.replace(/\//g, "");
    return number && message ? { number, message } : null;
  } catch {
    return null;
  }
}

/**
 * Explica cómo activar las notificaciones por WhatsApp. El QR es el camino
 * principal: la activación la hace el celular que quiere recibir los avisos, que
 * no tiene por qué ser el que está viendo la pantalla.
 */
export function WhatsappOptinDialog({
  open,
  onOpenChange,
  optinUrl,
  verified,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  optinUrl: string;
  verified: boolean;
}) {
  const target = parseOptinUrl(optinUrl);

  // El opt-in se completa fuera de la app (el usuario nos escribe), así que la
  // caída entre estos dos eventos es toda la fricción del canal.
  useEffect(() => {
    if (open) capture(verified ? "whatsapp_verified" : "whatsapp_optin_started");
  }, [open, verified]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        {verified ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="size-12 text-emerald-500" />
            <DialogTitle className="text-lg">
              ¡Listo! Ya estás activo
            </DialogTitle>
            <DialogDescription>
              Te avisaremos por WhatsApp de tus nuevas oportunidades y de los
              cambios en las licitaciones que sigues.
            </DialogDescription>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Activar WhatsApp</DialogTitle>
              <DialogDescription>
                Escanea el código con el celular que quieres activar.
              </DialogDescription>
            </DialogHeader>

            {/* Fondo blanco siempre: en dark mode un QR invertido no se escanea. */}
            <div className="mx-auto rounded-2xl border border-slate-200 bg-white p-4">
              <QRCodeSVG value={optinUrl} size={176} level="M" marginSize={0} />
            </div>

            <p className="text-center text-sm text-slate-500 dark:text-slate-400">
              Se abrirá un chat con el mensaje ya escrito: solo tienes que
              enviarlo. Queda verificado el celular desde el que nos escribas.
            </p>

            {target && (
              <p className="text-center text-xs text-slate-400 dark:text-slate-500">
                O escríbenos al <b>+{target.number}</b> el mensaje{" "}
                <b>{target.message}</b>
              </p>
            )}

            <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
              <span className="text-xs text-slate-400">o</span>
              <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
            </div>

            <a
              href={optinUrl}
              target="_blank"
              rel="noreferrer"
              className={WHATSAPP_BTN}
            >
              <WhatsappIcon />
              Abrir WhatsApp aquí
            </a>

            <p className="flex items-center justify-center gap-2 text-xs text-slate-400 dark:text-slate-500">
              <Loader2 className="size-3.5 animate-spin" />
              Esperando tu mensaje...
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
